import { createTheme } from "@mui/material/styles";

/**
 * Shufl design tokens.
 *
 * The look borrows Spotify's structure - near-black surfaces, a single saturated
 * accent, pill buttons, dense hover-highlighted rows - but the accent is Shufl
 * blue rather than Spotify green, and the greys carry a slight blue cast so the
 * accent feels native rather than bolted on.
 */
export const colors = {
    // Surfaces, darkest to lightest
    sidebar: "#000000",
    base: "#0B0F14",
    elevated: "#141A22",
    elevatedHover: "#1E2632",
    line: "#232B37",

    // Shufl blue - the green replacement
    accent: "#1D9BF0",
    accentHover: "#4FB4F7",
    accentPressed: "#1580CC",
    accentSoft: "rgba(29, 155, 240, 0.14)",

    textPrimary: "#FFFFFF",
    textSecondary: "#A8B2C1",
    textMuted: "#6C7787",

    danger: "#F2555A",
    success: "#1DD177",
} as const;

/** Gradient used behind playlist/page heroes, mirroring Spotify's fading header. */
export const heroGradient = `linear-gradient(180deg, ${colors.accentPressed} 0%, #12324A 45%, ${colors.base} 100%)`;

const fontStack = [
    "Circular",
    "-apple-system",
    "BlinkMacSystemFont",
    "Segoe UI",
    "Helvetica Neue",
    "Helvetica",
    "Arial",
    "sans-serif",
].join(",");

const theme = createTheme({
    palette: {
        mode: "dark",
        background: {
            default: colors.base,
            paper: colors.elevated,
        },
        primary: {
            main: colors.accent,
            dark: colors.accentPressed,
            light: colors.accentHover,
            contrastText: "#04121C",
        },
        secondary: {
            main: colors.elevatedHover,
            contrastText: colors.textPrimary,
        },
        error: { main: colors.danger },
        success: { main: colors.success },
        divider: colors.line,
        text: {
            primary: colors.textPrimary,
            secondary: colors.textSecondary,
        },
    },

    shape: { borderRadius: 8 },

    typography: {
        fontFamily: fontStack,
        // Spotify leans on very heavy, tight display type for headers.
        h1: { fontSize: "clamp(2.5rem, 7vw, 5.5rem)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.05 },
        h2: { fontSize: "clamp(1.75rem, 4vw, 3rem)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1 },
        h3: { fontSize: "2rem", fontWeight: 800, letterSpacing: "-0.02em" },
        h4: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.01em" },
        h5: { fontSize: "1.125rem", fontWeight: 700 },
        h6: { fontSize: "1rem", fontWeight: 700 },
        body1: { fontSize: "1rem", lineHeight: 1.6 },
        body2: { fontSize: "0.875rem", lineHeight: 1.6, color: colors.textSecondary },
        button: { fontWeight: 700, textTransform: "none", letterSpacing: "0.01em" },
    },

    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.base,
                    color: colors.textPrimary,
                    scrollbarColor: `${colors.line} transparent`,
                    "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
                        width: 12,
                        height: 12,
                        backgroundColor: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
                        borderRadius: 8,
                        backgroundColor: colors.line,
                        border: "3px solid transparent",
                        backgroundClip: "content-box",
                    },
                    "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#38424F",
                    },
                },
            },
        },

        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                // Spotify's signature: fully-rounded pill that scales up slightly on hover.
                root: {
                    borderRadius: 500,
                    padding: "10px 26px",
                    transition: "transform 120ms ease, background-color 120ms ease, opacity 120ms ease",
                    "&:hover": { transform: "scale(1.04)" },
                    "&:active": { transform: "scale(0.98)" },
                },
                containedPrimary: {
                    backgroundColor: colors.accent,
                    color: "#04121C",
                    "&:hover": { backgroundColor: colors.accentHover },
                },
                containedSecondary: {
                    backgroundColor: colors.elevatedHover,
                    color: colors.textPrimary,
                    "&:hover": { backgroundColor: "#2A3442" },
                },
                outlined: {
                    borderColor: "rgba(255,255,255,0.28)",
                    color: colors.textPrimary,
                    "&:hover": { borderColor: colors.textPrimary, backgroundColor: "transparent" },
                },
                text: {
                    color: colors.textSecondary,
                    "&:hover": { color: colors.textPrimary, backgroundColor: "transparent" },
                },
            },
        },

        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: "none", backgroundColor: colors.elevated },
            },
        },

        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: colors.elevatedHover,
                    borderRadius: 6,
                    marginTop: 8,
                    minWidth: 190,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
                },
                list: { padding: 4 },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    fontSize: 14,
                    fontWeight: 500,
                    padding: "10px 12px",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                },
            },
        },

        MuiTextField: {
            defaultProps: { variant: "filled" },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.elevatedHover,
                    borderRadius: 6,
                    overflow: "hidden",
                    "&:hover": { backgroundColor: "#26303D" },
                    "&.Mui-focused": {
                        backgroundColor: "#26303D",
                        boxShadow: `inset 0 0 0 2px ${colors.accent}`,
                    },
                    "&::before, &::after": { display: "none" },
                },
                input: { color: colors.textPrimary },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: { color: colors.textMuted, "&.Mui-focused": { color: colors.textSecondary } },
            },
        },

        MuiDivider: {
            styleOverrides: { root: { borderColor: colors.line } },
        },
    },
});

export default theme;
